import React from 'react';

import { useForm, FormProvider } from 'react-hook-form';
import { observer } from 'mobx-react';
import { enqueueSnackbar } from 'notistack';

import { ImportantDatesFrom } from '../../../../../components/form-sections/ImportantDatesFrom/ImportantDatesFrom';
import { WidgetHeaderActions } from '../components/WidgetHeaderActions/WidgetHeaderActions';
import { ChallengeImportantDatesFormValues } from '../../../../../interfaces/ChallengeFormValues';
import { loggedInUserDataStore } from '../../../../../../shared/stores/loggedInUserDataStore';
import { getImportantDatesFormValue } from './utils/getImportantDatesFormValue';
import { convertDatesToApiFormat } from '../../../../../utils/convertDatesToApiFormat';
import { challengeService } from '../../../../../services/challengeService';
import { useChallengeFormMode } from '../hook/useChallengeFormMode';
import { challengeProfileStore } from '../../../../../stores/challengeProfileStore';

type ImportantDatesWidgetProps = {
  challengeId: number;
};

const ImportantDatesWidget = observer(({ challengeId }: ImportantDatesWidgetProps) => {
  const { statusFlags, invalidSectionFlags, getChallengeStatusDetails } =
    challengeProfileStore;
  const { isEntity } = loggedInUserDataStore;
  const { mode, setEdit, setView } = useChallengeFormMode('importantDates');

  const formMethods = useForm<{ importantDates: ChallengeImportantDatesFormValues }>({
    defaultValues: async () => getImportantDatesFormValue(challengeId),
  });
  const { handleSubmit, reset } = formMethods;

  const handleSave = handleSubmit(async ({ importantDates }) => {
    const apiData = convertDatesToApiFormat(importantDates);
    if (!apiData) {
      enqueueSnackbar('Timezone is required', { variant: 'error' });
      return;
    }

    await challengeService.updateChallengeImportantDates(challengeId, apiData);

    reset({ importantDates });
    getChallengeStatusDetails(challengeId);
    setView();
  });

  const handleCancel = () => {
    reset();
    setView();
  };

  const headerActions = (
    <WidgetHeaderActions
      mode={mode}
      isInvalid={invalidSectionFlags.IMPORTANT_DATES}
      onEdit={setEdit}
      onCancel={handleCancel}
      onSubmit={handleSave}
    />
  );

  const canEdit = isEntity && statusFlags.DRAFT;

  return (
    <FormProvider {...formMethods}>
      <ImportantDatesFrom
        mode={mode}
        headerActions={canEdit ? headerActions : undefined}
      />
    </FormProvider>
  );
});

export { ImportantDatesWidget };
